import { jsx, jsxs } from "react/jsx-runtime";
import { memo, useState, useEffect } from "react";
import { KaTeX } from "../components/quiz-types/shared/katex-text.js";
import BlankBox from "../components/quiz-types/shared/blank-box.js";
function getImageUrl(source) {
  if (!source) return null;
  if (typeof source === "string") return source;
  return source.signedUrl ?? source.url ?? null;
}
function countBlanks(problem) {
  const reg = /\{\{([0-9]+)?}}/gim;
  let count = 0;
  while (reg.exec(problem) !== null) {
    count++;
  }
  return count;
}
function isAllBlanksFilled(answers) {
  return answers.every((answer) => answer !== null && answer !== void 0 && answer !== "");
}
const replaceCRLF = (t, id, imgs) => {
  const _id = id != null ? id : "tmp";
  const imageReg = /({image=\d+(?:,\s*[^}]+)*})/;
  const videoReg = /({video=https:\/\/www\.youtube\.com\/watch\?[^}]+})/;
  const withoutVideos = t.replace(videoReg, "");
  const parts = escape(withoutVideos).split(/(\n)/).flatMap((part) => part.split(imageReg).filter(Boolean));
  return parts.map((item, idx) => {
    if (item.indexOf("{image") !== -1 && imgs) {
      return /* @__PURE__ */ jsx(ImageWrap, { text: item, imgSources: imgs }, `image-${idx}`);
    } else {
      return /* @__PURE__ */ jsx("span", { children: item === "\n" ? /* @__PURE__ */ jsx("br", {}) : item }, _id + "_other_" + idx);
    }
  });
};
const ImageWrap = ({ text, imgSources }) => {
  const startIndex = text.indexOf("{image");
  const endIndex = text.indexOf("}");
  const rawString = text.substring(startIndex, endIndex + 1);
  const sliceString = rawString.replace(/{/g, "").replace(/}/g, "").split(",");
  let sizeString = null;
  let numberString = null;
  let spaceXString = null;
  let inlineString = false;
  const imageIndex = parseInt(sliceString[0].split("=")[1], 10);
  for (let i = 1; i < sliceString.length; i++) {
    if (sliceString[i].indexOf("scale=") !== -1) {
      sizeString = sliceString[i].replace(/scale=/, "");
    }
    if (sliceString[i].indexOf("number=") !== -1) {
      numberString = sliceString[i].replace(/number=/, "");
    }
    if (sliceString[i].indexOf("spaceX=") !== -1) {
      spaceXString = sliceString[i].replace(/spaceX=/, "");
    }
    if (sliceString[i].indexOf("inline") !== -1 && sliceString[i].split("=")[1] === "true") {
      inlineString = true;
    }
  }
  const source = imgSources[imageIndex];
  const url = getImageUrl(source);
  if (!url) return null;
  return /* @__PURE__ */ jsx(
    ExternalImage,
    {
      imgSrc: url,
      scale: sizeString ? Number(sizeString) : 1,
      number: numberString ? Number(numberString) : null,
      spaceX: spaceXString ? Number(spaceXString) : null,
      inline: inlineString
    }
  );
};
function ExternalImage({
  imgSrc,
  scale,
  number,
  spaceX,
  inline
}) {
  const [img, setImg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let isMounted = true;
    const _img = new Image();
    _img.onload = () => {
      if (isMounted) {
        setImg(_img);
        setLoaded(true);
      }
    };
    _img.onerror = () => {
    };
    _img.src = imgSrc;
    return () => {
      isMounted = false;
    };
  }, [imgSrc]);
  const style = {
    marginTop: 16,
    marginBottom: 16,
    display: inline ? "inline-flex" : "flex",
    justifyContent: "center",
    ...spaceX ? { marginLeft: spaceX, marginRight: spaceX } : {}
  };
  return /* @__PURE__ */ jsxs("div", { style, children: [
    number !== null && /* @__PURE__ */ jsxs("div", { style: { marginRight: 8 }, children: [
      "(",
      number,
      ")"
    ] }),
    loaded && img && /* @__PURE__ */ jsx("img", { src: img.src, width: img.width * scale + "px", alt: "quiz img" })
  ] });
}
const escape = (t) => {
  return t.replace(/＝/g, "=").replace(/－/g, "-").replace(/＋/g, "+").replace(/𝑚/g, "m").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"');
};
function formatText(text, id, imgs) {
  const _id = id != null ? id : "tmp";
  if (text == null) {
    return "";
  }
  const _t = text.normalize ? text.normalize("NFC") : text;
  let r;
  const katexIndices = [];
  let lastIndex = 0;
  const katexReg = new RegExp(/\$\$([^$]+)\$\$/gim);
  while (r = katexReg.exec(_t)) {
    if (r.index < lastIndex) {
      continue;
    }
    katexIndices.push(r.index, katexReg.lastIndex);
    lastIndex = katexReg.lastIndex;
  }
  katexIndices.push(_t.length);
  let lastIdx = 0;
  if (katexIndices.length > 0) {
    return katexIndices.map((i, seq) => {
      const pre = _t.substring(lastIdx, i);
      lastIdx = i;
      const key = _id + "_kat_" + seq;
      const match = katexReg.exec(escape(pre));
      if (match != null && match.index >= 0) {
        return /* @__PURE__ */ jsx(KaTeX, { math: match[1] }, key);
      } else {
        return /* @__PURE__ */ jsx("span", { className: "formatText_nonKatex", children: replaceCRLF(pre, id, imgs) }, key);
      }
    });
  } else {
    return /* @__PURE__ */ jsx("span", { className: "formateText", children: replaceCRLF(_t, id, imgs) });
  }
}
function replaceSquareBracketPattern(str) {
  const regex = /\[\](\{\{.*?\}\})/g;
  return str.replace(regex, "$1");
}
const ProblemTextChunk = memo(function ProblemTextChunk2({
  text,
  id,
  imgs,
  className = "formatProblem"
}) {
  return /* @__PURE__ */ jsx("span", { className, children: formatText(text, id, imgs) });
});
function formatProblem(problem, id, producer, imgs) {
  const _id = id != null ? id : "tmp";
  let _p = problem.normalize ? problem.normalize("NFC") : problem;
  _p = replaceSquareBracketPattern(_p);
  const totalReg = /(({\{(.*?)}})|(<\/?frac>)|(\/\/)|(<box>{.*?}<\/box>))/gim;
  const answerReg = /{\{(.*?)}}/gim;
  let r;
  const answerIndices = [];
  const parser = new RegExp(totalReg);
  let lastIdx = -1;
  while (r = parser.exec(_p)) {
    if (r.index === lastIdx) {
      answerIndices.push(parser.lastIndex);
    } else {
      answerIndices.push(r.index, parser.lastIndex);
    }
    lastIdx = parser.lastIndex;
  }
  answerIndices.push(_p.length);
  lastIdx = 0;
  let answerIdx = 0;
  if (answerIndices.length > 0) {
    let frac = false;
    let fracStartIdx = -1;
    const fracs = [];
    const reg = new RegExp(answerReg);
    let skip = 0;
    const result = answerIndices.map((i, seq) => {
      const pre = _p.substring(lastIdx, i);
      lastIdx = i;
      const key = _id + "_prob_" + seq;
      const _seq = seq - skip;
      const match = reg.exec(pre);
      if (match != null && match.index >= 0) {
        const a = producer(answerIdx, match[1]);
        answerIdx++;
        return a;
      } else {
        const t = pre.trim();
        if (t === "") {
          skip++;
          return null;
        }
        if (t === "<frac>") {
          frac = true;
          fracStartIdx = _seq;
          return "<frac>";
        } else if (t === "//") {
          return "<sep>";
        } else if (t === "</frac>") {
          if (frac) {
            frac = false;
            fracs.push([Number(fracStartIdx), Number(_seq)]);
            return "</frac>";
          }
        }
        const boxReg = /<box>\{(.*?)\}<\/box>/g;
        if (boxReg.test(pre)) {
          const boxContent = pre.replace(boxReg, "$1");
          return /* @__PURE__ */ jsx(BlankBox, { children: boxContent }, key + "blank_box");
        }
        return /* @__PURE__ */ jsx(
          ProblemTextChunk,
          {
            text: pre,
            id,
            imgs,
            className: "formatProblem"
          },
          key
        );
      }
    }).filter((o) => o != null);
    for (let i = 0; i < fracs.length; i++) {
      const fracItem = fracs[i];
      const startIdx = fracItem[0];
      const endIdx = fracItem[1];
      const numerator = [];
      const denominator = [];
      let sep = false;
      for (let j = startIdx + 1; j < endIdx; j++) {
        if (result[j] === "<sep>") {
          sep = true;
          continue;
        }
        if (result[j] === "" || result[j] === void 0) {
          continue;
        } else if (result[j] === "<frac>") {
          continue;
        } else if (result[j] === "</frac>") {
          sep = false;
          continue;
        }
        if (!sep) {
          numerator.push(result[j]);
        } else {
          denominator.push(result[j]);
        }
      }
      const component = /* @__PURE__ */ jsxs("div", { className: "problem-frac-wrap", children: [
        /* @__PURE__ */ jsx("div", { className: "problem-frac-numerator", children: numerator }),
        /* @__PURE__ */ jsx("div", { className: "problem-frac-denominator", children: denominator })
      ] }, _id + i);
      const len = endIdx - startIdx + 1;
      result.splice(startIdx, len, component);
      for (let j = i + 1; j < fracs.length; j++) {
        fracs[j][0] = fracs[j][0] - len + 1;
        fracs[j][1] = fracs[j][1] - len + 1;
      }
    }
    return result;
  } else {
    return [
      /* @__PURE__ */ jsx(
        ProblemTextChunk,
        {
          text: _p,
          id,
          imgs,
          className: "formatProblem_other"
        },
        "formatProblem_other"
      )
    ];
  }
}
export {
  countBlanks,
  formatProblem,
  formatText,
  isAllBlanksFilled,
  replaceSquareBracketPattern
};
//# sourceMappingURL=quiz-parser.js.map
